import { useEffect, useState, useMemo, useCallback } from 'react';

import { PageHeader, useWorkspace } from '../../components/layout/WorkspaceShell';
import Btn from '../../components/ui/Btn';
import { Card, Badge, Input, Spinner } from '../../components/ui/primitives';
import { Icon } from '../../components/ui/Icon';
import { ErrorBanner } from '../auth/SignInScreen';
import { supabase } from '../../lib/supabase';
import { useConfirm } from '../../lib/confirm';
import { useToast } from '../../lib/toast';
import { useIsMobile } from '../../lib/useBreakpoint';

// ────────────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────────────
const TEMPLATE_COLORS = ['#3b82f6', '#3dc75b', '#8b5cf6', '#f59e0b', '#14b8a6', '#ec4899', '#0a0f2e', '#ef4444'];

function colorForKey(key) {
  if (!key) return TEMPLATE_COLORS[0];
  const hash = String(key).split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return TEMPLATE_COLORS[hash % TEMPLATE_COLORS.length];
}

const CATEGORY_OPTIONS = [
  { value: '',            label: 'Uncategorized' },
  { value: 'identity',    label: 'Identity' },
  { value: 'tax',         label: 'Tax / Financial' },
  { value: 'insurance',   label: 'Insurance' },
  { value: 'legal',       label: 'Legal' },
  { value: 'medical',     label: 'Medical / Health' },
  { value: 'compliance',  label: 'Compliance' },
  { value: 'credentials', label: 'Credentials & Licensing' },
  { value: 'vehicle',     label: 'Vehicle' },
  { value: 'education',   label: 'Education' },
  { value: 'business',    label: 'Business / LLC' },
  { value: 'inservice',   label: 'In-Service Certificates' },
  { value: 'other',       label: 'Other' },
];

function toKey(label) {
  return (label || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s_-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .substring(0, 60);
}

// ────────────────────────────────────────────────────────────────────────────
// Main panel
// ────────────────────────────────────────────────────────────────────────────
export default function TemplatesPanel() {
  const { teamId, role, isOwner, refreshCounts } = useWorkspace();
  const isMobile = useIsMobile();
  const confirm = useConfirm();
  const toast = useToast();
  const canManage = isOwner || role?.can_manage_templates;

  const [templates, setTemplates]     = useState([]);
  const [roles, setRoles]             = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [uploadCounts, setUploadCounts] = useState({}); // template_id -> count
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [editingId, setEditingId]     = useState(null); // 'new' for add modal, otherwise template id

  // ── Load all the things ──
  const refresh = useCallback(async () => {
    console.log('[Templates] refresh called, teamId =', teamId);
    if (!teamId) {
      // Give a clear error if we have no team context — better than infinite spinner
      setError('No team context available. The workspace may still be loading.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    // Wrap each query so a hang becomes a visible error after 10s instead of an infinite spinner.
    // Also log when each completes so dev tools shows exactly which (if any) is slow.
    const withTimeout = (promise, label) => {
      const tStart = Date.now();
      return Promise.race([
        promise.then(r => {
          console.log(`[Templates] ${label} OK in ${Date.now() - tStart}ms`, r?.error ? '(returned error)' : '');
          return r;
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error(`${label} timed out after 10s`)), 10_000)
        ),
      ]);
    };

    try {
      const [tplRes, roleRes, assignRes, uploadRes] = await Promise.allSettled([
        withTimeout(
          supabase
            .from('document_templates')
            .select('*')
            .eq('team_id', teamId)
            .order('sort_order', { ascending: true })
            .order('label', { ascending: true }),
          'document_templates'
        ),
        withTimeout(
          supabase
            .from('team_roles')
            .select('id, name, slug, is_staff')
            .eq('team_id', teamId)
            .order('name'),
          'team_roles'
        ),
        withTimeout(
          supabase
            .from('template_role_assignments')
            .select('template_id, role_id')
            .eq('team_id', teamId),
          'template_role_assignments'
        ),
        withTimeout(
          supabase
            .from('document_uploads')
            .select('template_id')
            .eq('team_id', teamId),
          'document_uploads'
        ),
      ]);

      // Templates is the only required query
      if (tplRes.status === 'rejected') {
        throw tplRes.reason || new Error('Could not load templates.');
      }
      if (tplRes.value?.error) throw tplRes.value.error;

      const errs = [];
      if (roleRes.status === 'rejected' || roleRes.value?.error) {
        errs.push('roles');
        console.warn('Templates: roles query failed:', roleRes.reason || roleRes.value?.error);
      }
      if (assignRes.status === 'rejected' || assignRes.value?.error) {
        errs.push('role assignments');
        console.warn('Templates: assignments query failed:', assignRes.reason || assignRes.value?.error);
      }
      if (uploadRes.status === 'rejected' || uploadRes.value?.error) {
        // Non-fatal — we just won't have upload counts
        console.warn('Templates: upload counts query failed:', uploadRes.reason || uploadRes.value?.error);
      }

      setTemplates(tplRes.value.data || []);
      setRoles(roleRes.value?.data || []);
      setAssignments(assignRes.value?.data || []);

      const counts = {};
      const uploadRows = uploadRes.value?.data || [];
      uploadRows.forEach(u => {
        if (u.template_id) counts[u.template_id] = (counts[u.template_id] || 0) + 1;
      });
      setUploadCounts(counts);

      if (errs.length > 0) {
        setError(`Some data couldn't be loaded (${errs.join(', ')}). Open DevTools console for details.`);
      }
    } catch (err) {
      console.error('Templates load failed:', err);
      setError(err.message || 'Failed to load templates.');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Delete ──
  const deleteTemplate = async (tpl) => {
    if (!canManage) return;
    const used = uploadCounts[tpl.id] || 0;
    const msg = used > 0
      ? `${used} document${used === 1 ? '' : 's'} ${used === 1 ? 'is' : 'are'} classified as this template — they will become unmatched.`
      : 'This can\'t be undone.';
    const r = await confirm({
      title: `Delete "${tpl.label}"`,
      message: msg,
      confirmLabel: 'Delete template',
      confirmVariant: 'danger',
    });
    if (!r.confirmed) return;

    try {
      // Clean up assignments first (FK)
      await supabase
        .from('template_role_assignments')
        .delete()
        .eq('template_id', tpl.id);

      const { error: delErr } = await supabase
        .from('document_templates')
        .delete()
        .eq('id', tpl.id);
      if (delErr) throw delErr;

      setTemplates(templates.filter(t => t.id !== tpl.id));
      setAssignments(assignments.filter(a => a.template_id !== tpl.id));
      refreshCounts?.();
    } catch (err) {
      console.error('Delete template failed:', err);
      toast.error('Could not delete: ' + (err.message || 'unknown error'));
    }
  };

  // ── Render ──
  if (loading && templates.length === 0) {
    return (
      <>
        <PageHeader title="Templates" subtitle="Loading…" />
        <Card style={{ padding: 48, textAlign: 'center' }}>
          <Spinner size={20} color="var(--accent)" />
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Templates"
        subtitle={`${templates.length} template${templates.length === 1 ? '' : 's'} · Defines what documents you collect and which roles upload them.`}
        actions={
          canManage ? (
            <Btn variant="primary" size="md" icon={Icon.plus} onClick={() => setEditingId('new')}>
              New template
            </Btn>
          ) : null
        }
      />

      {error && <ErrorBanner message={error} style={{ marginBottom: 16 }} />}

      {!canManage && (
        <Card style={{ padding: 14, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon.info size={16} stroke="var(--text-3)" />
          <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
            You don't have permission to manage templates. Ask an Owner or Admin.
          </div>
        </Card>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile
          ? '1fr'
          : 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: isMobile ? 10 : 14,
      }}>
        {templates.map((tpl) => (
          <TemplateCard
            key={tpl.id}
            tpl={tpl}
            roles={roles}
            assignments={assignments.filter(a => a.template_id === tpl.id)}
            uploadCount={uploadCounts[tpl.id] || 0}
            canManage={canManage}
            onEdit={() => setEditingId(tpl.id)}
            onDelete={() => deleteTemplate(tpl)}
          />
        ))}

        {canManage && (
          <button
            type="button"
            onClick={() => setEditingId('new')}
            style={{
              border: '1.5px dashed var(--border-strong)',
              background: 'transparent',
              borderRadius: 'var(--r-lg)',
              padding: 18,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              textAlign: 'center', minHeight: 170,
              cursor: 'pointer', fontFamily: 'inherit',
              color: 'var(--text-3)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--accent)';
              e.currentTarget.style.background = 'var(--accent-bg)';
              e.currentTarget.style.color = 'var(--accent-600)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-strong)';
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--text-3)';
            }}
          >
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'var(--surface-2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 10,
            }}>
              <Icon.plus size={18} />
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
              Create a template
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
              Add a new doc type clients should upload
            </div>
          </button>
        )}
      </div>

      {editingId && (
        <TemplateEditModal
          mode={editingId === 'new' ? 'new' : 'edit'}
          template={editingId === 'new' ? null : templates.find(t => t.id === editingId)}
          existingAssignments={editingId === 'new' ? [] : assignments.filter(a => a.template_id === editingId)}
          roles={roles}
          teamId={teamId}
          onClose={() => setEditingId(null)}
          onSaved={() => { setEditingId(null); refresh(); refreshCounts?.(); }}
        />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Template card
// ────────────────────────────────────────────────────────────────────────────
function TemplateCard({ tpl, roles, assignments, uploadCount, canManage, onEdit, onDelete }) {
  const isMobile = useIsMobile();
  const assignedRoleIds = new Set(assignments.map(a => a.role_id));
  const assignedRoles = roles.filter(r => assignedRoleIds.has(r.id));
  const color = colorForKey(tpl.key || tpl.id);
  const initial = (tpl.label || '?').charAt(0).toUpperCase();

  return (
    <Card style={{ padding: isMobile ? 14 : 18, position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: color + '14', color: color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 17, fontWeight: 700, flexShrink: 0,
        }}>
          {initial}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 700, marginBottom: 2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            color: 'var(--text)',
          }}>
            {tpl.label}
          </div>
          {tpl.label_es && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
              ES: {tpl.label_es}
            </div>
          )}
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>
            {CATEGORY_OPTIONS.find(c => c.value === (tpl.category || ''))?.label || 'Uncategorized'}
          </div>
        </div>
      </div>

      <div style={{
        display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap',
      }}>
        {tpl.required && <Badge variant="accent" size="xs">Required</Badge>}
        {tpl.has_expiration && <Badge variant="warn" size="xs"><Icon.clock size={9} /> Expires</Badge>}
        {assignedRoles.length === 0 ? (
          <Badge variant="neutral" size="xs">No roles</Badge>
        ) : (
          assignedRoles.map(r => (
            <Badge key={r.id} variant={r.is_staff ? 'info' : 'neutral'} size="xs">
              {r.name}
            </Badge>
          ))
        )}
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 0 0', borderTop: '1px solid var(--border)',
        marginTop: 'auto',
      }}>
        <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
          <strong style={{ color: 'var(--text)', fontWeight: 600 }}>{uploadCount}</strong> collected
        </span>
        {canManage && (
          <div style={{ display: 'flex', gap: 4 }}>
            <Btn variant="ghost" size="xs" icon={Icon.edit} onClick={onEdit}>Edit</Btn>
            <Btn variant="ghost" size="xs" icon={Icon.trash} onClick={onDelete} title="Delete" />
          </div>
        )}
      </div>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Edit / new modal
// ────────────────────────────────────────────────────────────────────────────
function TemplateEditModal({ mode, template, existingAssignments, roles, teamId, onClose, onSaved }) {
  const isNew = mode === 'new';
  const isMobile = useIsMobile();

  const [label, setLabel]                 = useState(template?.label || '');
  const [labelEs, setLabelEs]             = useState(template?.label_es || '');
  const [keyVal, setKeyVal]               = useState(template?.key || '');
  const [keyEdited, setKeyEdited]         = useState(!isNew); // only auto-derive on new
  const [category, setCategory]           = useState(template?.category || '');
  const [required, setRequired]           = useState(template?.required ?? true);
  const [hasExpiration, setHasExpiration] = useState(template?.has_expiration ?? false);
  const [selectedRoleIds, setSelectedRoleIds] = useState(
    new Set(existingAssignments.map(a => a.role_id))
  );

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  // Auto-derive key from label on new templates until user edits the key field
  useEffect(() => {
    if (isNew && !keyEdited) setKeyVal(toKey(label));
  }, [label, isNew, keyEdited]);

  const toggleRole = (roleId) => {
    const next = new Set(selectedRoleIds);
    if (next.has(roleId)) next.delete(roleId); else next.add(roleId);
    setSelectedRoleIds(next);
  };

  const submit = async () => {
    setError(null);
    if (!label.trim()) { setError('Please enter a template name.'); return; }
    const finalKey = (keyVal || toKey(label)).trim();
    if (!finalKey) { setError('Template key cannot be empty.'); return; }

    setSaving(true);
    try {
      let templateId;

      if (isNew) {
        const { data: inserted, error: insErr } = await supabase
          .from('document_templates')
          .insert({
            team_id: teamId,
            label: label.trim(),
            label_es: labelEs.trim() || null,
            key: finalKey,
            category: category || null,
            required,
            has_expiration: hasExpiration,
            sort_order: 0,
          })
          .select()
          .single();
        if (insErr) throw insErr;
        templateId = inserted.id;
      } else {
        templateId = template.id;
        const { error: updErr } = await supabase
          .from('document_templates')
          .update({
            label: label.trim(),
            label_es: labelEs.trim() || null,
            key: finalKey,
            category: category || null,
            required,
            has_expiration: hasExpiration,
          })
          .eq('id', templateId);
        if (updErr) throw updErr;
      }

      // Reconcile role assignments: compute additions and removals
      const previousRoleIds = new Set(existingAssignments.map(a => a.role_id));
      const toAdd    = [...selectedRoleIds].filter(id => !previousRoleIds.has(id));
      const toRemove = [...previousRoleIds].filter(id => !selectedRoleIds.has(id));

      if (toRemove.length > 0) {
        const { error: rmErr } = await supabase
          .from('template_role_assignments')
          .delete()
          .eq('template_id', templateId)
          .in('role_id', toRemove);
        if (rmErr) throw rmErr;
      }

      if (toAdd.length > 0) {
        const { error: addErr } = await supabase
          .from('template_role_assignments')
          .insert(toAdd.map(roleId => ({
            template_id: templateId,
            role_id: roleId,
            team_id: teamId,
          })));
        if (addErr) throw addErr;
      }

      onSaved();
    } catch (err) {
      console.error('Save template failed:', err);
      const msg = (err.message || '').toLowerCase().includes('duplicate')
        ? 'That key is already used by another template. Try a different key.'
        : (err.message || 'Could not save template.');
      setError(msg);
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(10, 15, 46, 0.55)',
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: isMobile ? 'none' : '1px solid var(--border)',
          borderRadius: isMobile ? 0 : 'var(--r-xl)',
          boxShadow: 'var(--shadow-xl)',
          width: '100%',
          maxWidth: isMobile ? 'none' : 540,
          maxHeight: isMobile ? '100dvh' : '90vh',
          height: isMobile ? '100dvh' : 'auto',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{
          padding: isMobile ? '16px 20px' : '20px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexShrink: 0,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>
              {isNew ? 'New template' : 'Edit template'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
              Define a document type clients (or staff) should upload.
            </div>
          </div>
          <Btn variant="ghost" size="sm" icon={Icon.x} onClick={onClose} title="Close" />
        </div>

        <div style={{ padding: isMobile ? '16px 20px' : '20px 24px', overflowY: 'auto', flex: 1 }}>
          {error && <ErrorBanner message={error} style={{ marginBottom: 14 }} />}

          <FieldLabel>Name</FieldLabel>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Proof of Insurance, W-9 Form"
            disabled={saving}
            style={{ marginBottom: 12 }}
          />

          <FieldLabel optional>Spanish name (optional)</FieldLabel>
          <Input
            value={labelEs}
            onChange={(e) => setLabelEs(e.target.value)}
            placeholder="e.g. Comprobante de seguro"
            disabled={saving}
            style={{ marginBottom: 12 }}
          />

          <FieldLabel>Key</FieldLabel>
          <Input
            value={keyVal}
            onChange={(e) => { setKeyVal(toKey(e.target.value)); setKeyEdited(true); }}
            placeholder="proof_of_insurance"
            disabled={saving}
            style={{ marginBottom: 4, fontFamily: 'var(--font-mono)' }}
          />
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 14 }}>
            Stable identifier used by the AI classifier. Lowercase letters, numbers, underscores only.
          </div>

          <FieldLabel optional>Category (optional)</FieldLabel>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={saving}
            className="mi-focusable"
            style={{
              width: '100%', height: 40, padding: '0 12px',
              fontSize: 13.5, fontFamily: 'inherit', color: 'var(--text)',
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)', cursor: 'pointer', marginBottom: 14,
            }}
          >
            {CATEGORY_OPTIONS.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 8, marginBottom: 18,
          }}>
            <ToggleSwitch
              label="Required"
              hint="Members must upload this"
              checked={required}
              onChange={setRequired}
              disabled={saving}
            />
            <ToggleSwitch
              label="Has expiration"
              hint="Track expiration date + send reminders"
              checked={hasExpiration}
              onChange={setHasExpiration}
              disabled={saving}
            />
          </div>

          <FieldLabel>Assigned to roles</FieldLabel>
          {roles.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>
              No roles defined yet. Create roles from the Roles tab first.
            </div>
          ) : (
            <RolePicker
              roles={roles}
              selectedIds={selectedRoleIds}
              onToggle={toggleRole}
              onSelectAll={() => {
                // Select all if not all are selected; otherwise clear all.
                // Using setSelectedRoleIds directly (not toggleRole in loop) to
                // avoid React batching issues where each call sees stale state.
                if (selectedRoleIds.size === roles.length) {
                  setSelectedRoleIds(new Set());
                } else {
                  setSelectedRoleIds(new Set(roles.map(r => r.id)));
                }
              }}
              disabled={saving}
            />
          )}
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>
            Members in selected roles will be expected to upload this template.
          </div>
        </div>

        <div style={{
          padding: isMobile ? '14px 20px' : '16px 24px',
          borderTop: '1px solid var(--border)',
          display: 'flex', gap: 8,
          justifyContent: isMobile ? 'stretch' : 'flex-end',
          flexShrink: 0,
          background: 'var(--surface)',
        }}>
          <Btn
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={saving}
            style={isMobile ? { flex: 1, justifyContent: 'center' } : undefined}
          >
            Cancel
          </Btn>
          <Btn
            variant="primary"
            size="md"
            iconRight={saving ? null : Icon.check}
            onClick={submit}
            disabled={saving || !label.trim()}
            style={isMobile ? { flex: 2, justifyContent: 'center' } : undefined}
          >
            {saving ? <><Spinner size={14} color="rgba(255,255,255,0.6)" /> Saving…</> : (isNew ? 'Create template' : 'Save changes')}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────
function FieldLabel({ children, optional }) {
  return (
    <label style={{
      fontSize: 12, fontWeight: 600, color: 'var(--text-2)',
      display: 'block', marginBottom: 6,
    }}>
      {children}
      {optional && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · optional</span>}
    </label>
  );
}

function ToggleSwitch({ label, hint, checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{
        flex: 1, padding: '10px 12px',
        background: checked ? 'var(--accent-bg)' : 'var(--surface-2)',
        border: `1.5px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
        borderRadius: 'var(--r-md)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left', fontFamily: 'inherit',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{label}</span>
        <span style={{
          width: 30, height: 18, borderRadius: 9, position: 'relative',
          background: checked ? 'var(--accent)' : 'var(--border-strong)',
          transition: 'background 0.15s',
        }}>
          <span style={{
            position: 'absolute', top: 2, left: checked ? 14 : 2,
            width: 14, height: 14, borderRadius: '50%',
            background: '#fff', transition: 'left 0.15s',
          }} />
        </span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{hint}</div>
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// RolePicker — select-all, staff/non-staff grouping, compact for many roles
// ────────────────────────────────────────────────────────────────────────────
function RolePicker({ roles, selectedIds, onToggle, onSelectAll, disabled }) {
  const staffRoles = useMemo(() => roles.filter(r => r.is_staff), [roles]);
  const nonStaffRoles = useMemo(() => roles.filter(r => !r.is_staff), [roles]);
  const allSelected = selectedIds.size === roles.length && roles.length > 0;
  const noneSelected = selectedIds.size === 0;

  // Use compact dropdown mode when there are 8+ roles
  const compact = roles.length >= 8;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const renderChip = (r) => {
    const sel = selectedIds.has(r.id);
    return (
      <button
        key={r.id}
        type="button"
        onClick={() => onToggle(r.id)}
        disabled={disabled}
        style={{
          padding: '5px 10px', fontSize: 12, fontWeight: 600,
          border: `1.5px solid ${sel ? 'var(--accent)' : 'var(--border)'}`,
          background: sel ? 'var(--accent-bg)' : 'var(--surface)',
          color: sel ? 'var(--accent-600)' : 'var(--text-2)',
          borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
          display: 'inline-flex', alignItems: 'center', gap: 5,
          transition: 'all 0.12s',
        }}
      >
        {sel && <Icon.check size={11} />}
        {r.name}
      </button>
    );
  };

  const renderGroup = (title, list) => {
    if (list.length === 0) return null;
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{
          fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)',
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
        }}>
          {title} · {list.length}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {list.map(renderChip)}
        </div>
      </div>
    );
  };

  if (compact) {
    // Dropdown mode: summary + expandable panel with search
    const filtered = search.trim()
      ? roles.filter(r => r.name.toLowerCase().includes(search.toLowerCase()))
      : roles;
    const filteredStaff = filtered.filter(r => r.is_staff);
    const filteredNonStaff = filtered.filter(r => !r.is_staff);

    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            disabled={disabled}
            style={{
              flex: 1, padding: '9px 12px', fontSize: 13, textAlign: 'left',
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)', cursor: 'pointer', fontFamily: 'inherit',
              color: selectedIds.size > 0 ? 'var(--text)' : 'var(--text-muted)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <span>
              {selectedIds.size === 0
                ? 'No roles assigned'
                : allSelected
                  ? `All ${roles.length} roles`
                  : `${selectedIds.size} of ${roles.length} roles selected`}
            </span>
            <Icon.chevR size={13} style={{ transform: open ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.15s' }} />
          </button>
          <button
            type="button"
            onClick={onSelectAll}
            disabled={disabled}
            style={{
              padding: '8px 12px', fontSize: 12, fontWeight: 600,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)', cursor: 'pointer', fontFamily: 'inherit',
              color: 'var(--text-2)', whiteSpace: 'nowrap',
            }}
          >
            {allSelected ? 'Clear all' : 'Select all'}
          </button>
        </div>

        {open && (
          <div style={{
            border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
            padding: 12, background: 'var(--surface)',
            maxHeight: 300, overflowY: 'auto',
          }}>
            <Input
              icon={Icon.search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search roles…"
              style={{ marginBottom: 10 }}
            />
            {filtered.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '8px 4px' }}>
                No roles match "{search}"
              </div>
            ) : (
              <>
                {renderGroup('Staff', filteredStaff)}
                {renderGroup('Non-staff', filteredNonStaff)}
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // Compact chip mode: just show all roles in groups, with select-all button
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button
          type="button"
          onClick={onSelectAll}
          disabled={disabled}
          style={{
            padding: '3px 10px', fontSize: 11, fontWeight: 600,
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontFamily: 'inherit', color: 'var(--accent-600)',
          }}
        >
          {allSelected ? 'Clear all' : 'Select all'}
        </button>
      </div>
      {renderGroup('Staff', staffRoles)}
      {renderGroup('Non-staff', nonStaffRoles)}
    </div>
  );
}
